import { Injectable, inject } from '@angular/core';

import { BetSlipType } from '@bpos';
import { CrmConfig, Sitecore, SitecoreTokenCardDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { OddsFormatterService } from '@frontend/sports/odds/feature';
import { CrmTokenToQualifyReplaceParameters } from '@frontend/sports/types/components/crm-tokens';
import { TokenCardDetailsReplaceParameters } from '@frontend/sports/types/configuration/client-configuration';
import { UserOddsFormatSettingsService } from '@frontend/sports/user/feature';
import { camelCase } from 'lodash-es';

import { BetStatus, RewardTokenType, RewardTriggerType } from '../../../../lib/tokens-base/token-base.models';
import { IRewardToken, VIPExclusiveDetails } from '../reward-tokens.model';
import { TokenCategories } from './reward-tokens-banner.service';
import { RewardTokensFormattersService } from './reward-tokens-formatters.service';
import { isBetBuilderExclusiveToken, isBetBuilderToken, mapOdds } from './reward-tokens.utils';

@Injectable()
export class RewardTokensCardDetailsService {
    constructor(
        private crmConfig: CrmConfig,
        private sitecoreCardDetailsConfig: SitecoreTokenCardDetailsConfig,
        private tokenFormatterService: RewardTokensFormattersService,
        private sitecore: Sitecore,
    ) {}

    protected readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);
    protected readonly oddsFormatterService = inject(OddsFormatterService);

    tokenCustomDescription(token: IRewardToken) {
        const replaceableParams = this.crmConfig.tokens.toQualifyReplaceParameters;
        const replaceParameters = [
            RewardTokenType.BetAndGet,
            RewardTokenType.OddsBoost,
            RewardTokenType.FreeBet,
            RewardTokenType.RiskFreeBet,
        ].includes(token.rewardTokenType)
            ? replaceableParams[camelCase(token.rewardTokenType) as keyof CrmTokenToQualifyReplaceParameters]
            : {};

        return this.replaceTokenDescriptionValue(token, replaceParameters);
    }

    tokenToQualifyData(token: IRewardToken, categories: TokenCategories): string[] {
        const toQualifyEntity = this.toQualifyReplaceableParameters(token.rewardTokenType);
        const toQualifyReplacedData: string[] = [];
        if (!toQualifyEntity) {
            return toQualifyReplacedData;
        }
        if (this.sitecoreCardDetailsConfig.messages) {
            const applicableSports = interpolateDoubleBracesString(this.sitecoreCardDetailsConfig.messages.applicableSportsTitle, {
                applicableSport: categories.sportsCategory || '',
            });
            toQualifyReplacedData.push(applicableSports);

            const applicableOnCategoriesList =
                categories.liveCategory || categories.competitionsCategory || categories.meetingsCategory || categories.fixturesCategory;
            if (applicableOnCategoriesList) {
                const applicableOnCategories = interpolateDoubleBracesString(this.sitecoreCardDetailsConfig.messages.applicableOnTitle, {
                    applicableOnCategoriesList: applicableOnCategoriesList || '',
                });
                toQualifyReplacedData.push(applicableOnCategories);
            }

            const applicableOnMarketsList =
                categories.marketsCategory || categories.marketTemplatesCategory || categories.marketParametersCategory || categories.optionsCategory;
            if (applicableOnMarketsList) {
                const applicableMarketsCategories = interpolateDoubleBracesString(this.sitecoreCardDetailsConfig.messages.applicableMarketsTitle, {
                    applicableOnMarkets: applicableOnMarketsList || '',
                });
                toQualifyReplacedData.push(applicableMarketsCategories);
            }

            toQualifyReplacedData.push(this.replaceQualifyEntity(token, this.sitecoreCardDetailsConfig.messages.betOnTitle, toQualifyEntity.betOn));

            toQualifyReplacedData.push(this.replaceBetTypeQualifyEntity(token, this.sitecoreCardDetailsConfig.messages.betTypeTitle));
        }

        if (token.minimumLegs && this.sitecoreCardDetailsConfig.messages) {
            const minimumLegs = this.replaceQualifyEntity(
                token,
                this.sitecoreCardDetailsConfig.messages.minimumLegsTitle,
                toQualifyEntity.minimumLegs,
            );
            toQualifyReplacedData.push(minimumLegs);
        }

        if (token.selectionLevelMinimumOdds && this.sitecoreCardDetailsConfig.messages) {
            const selectionLevelMinimumOddsDetails = this.replaceQualifyEntity(
                token,
                this.sitecoreCardDetailsConfig.messages.selectionLevelMinOddsTitle,
                this.oddsFormatterService.format(mapOdds(token.selectionLevelMinimumOdds), {
                    displayFormat: this.userOddsFormatSettingsService.oddsFormat,
                }),
            );
            toQualifyReplacedData.push(selectionLevelMinimumOddsDetails);
        }

        if (token.minimumOdds && this.sitecoreCardDetailsConfig.messages) {
            const minimumOverallOdds = this.replaceQualifyEntity(
                token,
                this.sitecoreCardDetailsConfig.messages.minimumOverallOddsTitle,
                this.oddsFormatterService.format(mapOdds(token.minimumOdds), {
                    displayFormat: this.userOddsFormatSettingsService.oddsFormat,
                }),
            );
            toQualifyReplacedData.push(minimumOverallOdds);
        }

        if (token.selectionLevelMaximumOdds && this.sitecoreCardDetailsConfig.messages) {
            const selectionLevelMaximumOddsDetails = this.replaceQualifyEntity(
                token,
                this.sitecoreCardDetailsConfig.messages.selectionLevelMaxOddsTitle,
                this.oddsFormatterService.format(mapOdds(token.selectionLevelMaximumOdds), {
                    displayFormat: this.userOddsFormatSettingsService.oddsFormat,
                }),
            );
            toQualifyReplacedData.push(selectionLevelMaximumOddsDetails);
        }

        if (token.maximumOdds && this.sitecoreCardDetailsConfig.messages) {
            const maximumOverallOdds = this.replaceQualifyEntity(
                token,
                this.sitecoreCardDetailsConfig.messages.maximumOverallOddsTitle,
                this.oddsFormatterService.format(mapOdds(token.maximumOdds), {
                    displayFormat: this.userOddsFormatSettingsService.oddsFormat,
                }),
            );
            toQualifyReplacedData.push(maximumOverallOdds);
        }

        let rewardIssuedOn;
        if ('rewardTriggerType' in token && this.sitecoreCardDetailsConfig.messages) {
            const rewardTriggerType = token.rewardTriggerType;
            switch (rewardTriggerType) {
                case RewardTriggerType.BetPlacement:
                    rewardIssuedOn = this.sitecoreCardDetailsConfig.messages.betPlacementTemplate;
                    break;
                case RewardTriggerType.BetSettlement:
                    switch ('betStatus' in token && token.betStatus) {
                        case BetStatus.Any:
                            rewardIssuedOn = this.sitecoreCardDetailsConfig.messages.betSettlementTemplateForAny;
                            break;
                        case BetStatus.Win:
                            rewardIssuedOn = this.sitecoreCardDetailsConfig.messages.betSettlementTemplateForWin;
                            break;
                        case BetStatus.Loss:
                            rewardIssuedOn = this.sitecoreCardDetailsConfig.messages.betSettlementTemplateForLoss;
                            break;
                        default:
                            rewardIssuedOn = '';
                    }
                    break;
                default:
                    rewardIssuedOn = '';
            }
        } else {
            rewardIssuedOn = toQualifyEntity.issuedOn;
        }

        const issuedOn = this.replaceQualifyEntity(token, this.sitecoreCardDetailsConfig.messages?.issuedOnTitle ?? '', rewardIssuedOn);
        if (issuedOn) {
            toQualifyReplacedData.push(issuedOn);
        }

        return toQualifyReplacedData.map((data) => data.replaceAll(',', ' | '));
    }

    getTokenType(rewardTokenType: string): string {
        const tokenParameters = this.toQualifyReplaceableParameters(rewardTokenType);

        return tokenParameters?.tokenType || '';
    }

    getVIPExclusiveContent(): VIPExclusiveDetails {
        return {
            badge: this.sitecore.crmTokens.messages.VIPBadgeIcon,
            badgeSize: this.sitecore.crmTokens.messages.VIPBadgeIconSize.length ? this.sitecore.crmTokens.messages.VIPBadgeIconSize : '23',
            badgeTitle: this.sitecore.crmTokens.messages.VIPExclusiveText,
        };
    }

    isVIPBadgeEnabled(): boolean {
        return this.crmConfig.tokens.isVIPBadgeEnabled;
    }

    private toQualifyReplaceableParameters(rewardTokenType: string): TokenCardDetailsReplaceParameters | undefined {
        switch (rewardTokenType) {
            case RewardTokenType.BetAndGet:
                return this.sitecoreCardDetailsConfig.betAndGetCardDetailsQualify;
            case RewardTokenType.OddsBoost:
                return this.sitecoreCardDetailsConfig.oddsBoostCardDetailsQualify;
            case RewardTokenType.FreeBet:
                return this.sitecoreCardDetailsConfig.freeBetCardDetailsQualify;
            case RewardTokenType.RiskFreeBet:
                return this.sitecoreCardDetailsConfig.riskFreeCardDetailsQualify;
        }

        return;
    }

    private replaceTokenDescriptionValue(token: IRewardToken, replaceParameters?: StringDictionary<string>) {
        let description = token.customDescription!;
        if (replaceParameters) {
            Object.entries(replaceParameters).forEach((item) => {
                const [key, value] = item;
                description = description.replace(key, value);
            });
        }

        return this.tokenFormatterService.getTokenQuickViewReplaceValue(token, description);
    }

    private replaceQualifyEntity(token: IRewardToken, replaceDescription: string, replaceVlaue: string | undefined): string {
        const replacedValue = this.tokenFormatterService.getTokenQuickViewReplaceValue(token, replaceVlaue);

        return replacedValue ? interpolateDoubleBracesString(replaceDescription, { value: replacedValue }) : '';
    }

    private replaceBetTypeQualifyEntity(token: IRewardToken, replaceDescription: string): string {
        const replacedValue = this.getBetTypeDetails(token);

        return interpolateDoubleBracesString(replaceDescription, { value: replacedValue });
    }

    private getBetTypeDetails(token: IRewardToken): string {
        if (isBetBuilderExclusiveToken(token)) {
            return this.sitecore.crmTokens.cards.BuildABet;
        }

        return this.getBetTypeName(token);
    }

    private getBetTypeName(token: IRewardToken): string {
        const isBetBuilder = isBetBuilderToken(token);
        const betSlipType = token.filter.betSlipType;

        if (isBetBuilder) {
            switch (betSlipType) {
                case BetSlipType.Single:
                    return this.sitecore.crmTokens.cards.SingleBAB;
                case BetSlipType.Combo:
                    return this.sitecore.crmTokens.cards.MultipleBAB;
                default:
                    return this.sitecore.crmTokens.cards.BuildABetAny;
            }
        } else {
            switch (betSlipType) {
                case BetSlipType.Single:
                    return this.sitecore.crmTokens.cards.Single;
                case BetSlipType.Combo:
                    return this.sitecore.crmTokens.cards.Multiple;
                default:
                    return this.sitecore.crmTokens.cards.SingleMultiple;
            }
        }
    }
}
